/**
 * Бюджетные справочники сезона — слой над `/api/v1/planning/budget-catalog/*`
 * (backend PR #764, ADR-065).
 *
 * Экран собран по УТВЕРЖДЁННОМУ founder макету
 * `docs/superpowers/prototypes/annual-planning/v2-01-budget-catalogs.html`
 * (ветка `docs/wireframes-season-planning-v2`) и правкам founder 15.08
 * (`docs/superpowers/specs/2026-08-15-budget-catalogs-founder-edits.md`):
 *
 *   • внутри ТМЦ — подвкладки по категориям: СЗР / Семена / Удобрения / ГСМ /
 *     Прочее. Одна категория — один список, общего свала нет (порция 1);
 *   • колонки «рекомендуемая норма» нет вовсе (порция 1);
 *   • колонка называется «Системный справочник», статус сопоставления —
 *     значком в строке, а не отдельной колонкой «Сматчено с» (порция 1);
 *   • расценки работ, аутсорс и цены готовой продукции ведутся В РАЗРЕЗЕ
 *     ХОЗЯЙСТВА; строка без хозяйства = шаблон компании (порция 3);
 *   • закупки ТМЦ — на компанию, с ОПЦИЕЙ цены для хозяйства на позиции
 *     (порция 4).
 *
 * Язык интерфейса — экономиста: «позиция бюджета», «плановая цена», «цена для
 * хозяйства», «убрать из бюджета». Модельных слов (section / match_status)
 * наружу не отдаём.
 */
import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiError, API_URL } from '@/lib/api';
import { describeApiError } from '@/lib/apiError';
import { toast } from '@/lib/toast';

// ── Разделы ──────────────────────────────────────────────────────────────────

/**
 * Разделы бюджетного справочника — зеркало `SECTIONS` бэкенда.
 *
 * `sale_product` тут остаётся ради совместимости с API (в тенантах есть старые
 * позиции), но ВКЛАДКИ у него больше нет (AK-769): цены реализации ведутся в
 * «Ценах и объёмах реализации» (`company_sale_prices`) — только оттуда их
 * читает план по выручке. Не возвращай вкладку, не переключив сначала
 * читателей: правка в ней до выручки не доедет.
 */
export type BudgetSection =
  | 'pesticide'
  | 'seed'
  | 'fertilizer'
  | 'fuel'
  | 'other_tmc'
  | 'work_rate'
  | 'outsource'
  | 'sale_product';

/** Подвкладки ТМЦ в порядке макета (порция 1 founder). */
export const TMC_SECTIONS: readonly BudgetSection[] = [
  'pesticide',
  'seed',
  'fertilizer',
  'fuel',
  'other_tmc',
];

export const SECTION_TITLES: Record<BudgetSection, string> = {
  pesticide: 'СЗР',
  seed: 'Семена',
  fertilizer: 'Удобрения',
  fuel: 'ГСМ',
  other_tmc: 'Прочее',
  work_rate: 'Расценки работ и топливо',
  outsource: 'Аутсорс — работы подрядчика',
  sale_product: 'Готовая продукция · классы качества',
};

/** Разделы, которые ведутся по хозяйству (порция 3 founder). */
export const FARM_LEVEL_SECTIONS: readonly BudgetSection[] = [
  'work_rate',
  'outsource',
  'sale_product',
];

export function isFarmLevel(section: BudgetSection): boolean {
  return FARM_LEVEL_SECTIONS.includes(section);
}

/**
 * Подсказки для поля «Тип» позиции (AK-656 · UAT founder 18.08).
 *
 * `item_kind` в БД остаётся свободным текстом — это НЕ enum и не миграция.
 * Список нужен интерфейсу: экономист выбирает из знакомых слов вместо того,
 * чтобы каждый раз печатать «гербицид» с новой опечаткой, а нестандартное
 * значение вводит своим (выпадашка creatable, см. `ItemKindSelect`).
 *
 * Порядок здесь не важен — выпадашка сортирует по алфавиту (сквозное правило).
 *
 * Чего тут нет и почему:
 *  • `seed` — «тип» у семян это КУЛЬТУРА, ей нужен справочник культур, а не
 *    словарик из головы; до отдельной задачи поле остаётся текстовым;
 *  • `sale_product` — у раздела вместо типа «класс качества».
 * У СЗР словарь — только запасной: основной источник канонический,
 * `reference.pesticide_categories` (см. `ItemKindSelect`).
 */
export const SECTION_ITEM_KINDS: Partial<Record<BudgetSection, readonly string[]>> = {
  pesticide: [
    'Гербицид',
    'Фунгицид',
    'Инсектицид',
    'Акарицид',
    'Протравитель',
    'Десикант',
    'Дефолиант',
    'Родентицид',
    'Регулятор роста',
    'Прилипатель (адъювант)',
    'Биопрепарат',
  ],
  fertilizer: [
    'Азотное',
    'Фосфорное',
    'Калийное',
    'Комплексное',
    'Микроудобрение',
    'Органическое',
    'Мелиорант',
    'Биопрепарат',
  ],
  fuel: [
    'Дизельное топливо',
    'Бензин',
    'Газ',
    'Моторное масло',
    'Трансмиссионное масло',
    'Гидравлическое масло',
    'Смазка',
    'Антифриз',
    'Мочевина для SCR',
  ],
  other_tmc: [
    'Запчасти',
    'Шпагат и сетка',
    'Плёнка',
    'Тара и упаковка',
    'Инвентарь',
    'Спецодежда',
    'Хозтовары',
  ],
  work_rate: [
    'Обработка почвы',
    'Посев',
    'Внесение удобрений',
    'Защита растений',
    'Уборка',
    'Транспортные работы',
    'Погрузочно-разгрузочные работы',
    'Мелиорация',
  ],
  outsource: [
    'Обработка почвы',
    'Посев',
    'Внесение удобрений',
    'Защита растений',
    'Уборка',
    'Транспортные услуги',
    'Сушка и подработка',
    'Хранение',
  ],
};

/** У каких разделов вообще есть системный справочник (зеркало бэка). */
const NO_SYSTEM_CATALOG: readonly BudgetSection[] = ['fuel', 'other_tmc', 'work_rate', 'outsource'];

export function hasSystemCatalog(section: BudgetSection): boolean {
  return !NO_SYSTEM_CATALOG.includes(section);
}

/**
 * Откуда берём эталон для сопоставления. `null` — системного каталога для
 * раздела на платформе пока нет (удобрения), позиция живёт как «своя».
 */
export function systemCatalogSource(
  section: BudgetSection,
): { path: string; translated?: boolean; label: string } | null {
  switch (section) {
    case 'pesticide':
      return { path: '/api/v1/reference/pesticides/products', label: 'препарат из каталога СЗР' };
    case 'seed':
      return { path: '/api/v1/reference/varieties', translated: true, label: 'сорт или гибрид' };
    case 'sale_product':
      return { path: '/api/v1/reference/crops', translated: true, label: 'культура' };
    default:
      return null;
  }
}

// ── Позиция бюджета ──────────────────────────────────────────────────────────

export type MatchStatus =
  | 'matched'
  | 'suggested'
  | 'queued'
  | 'pending_approval'
  | 'not_applicable'
  | 'own';

export interface BudgetFarmPrice {
  farm_id: string;
  farm_name: string | null;
  price: number;
  price_unit_id: string | null;
  currency: string;
  price_source: string | null;
}

/**
 * Подсказка резолвера для непривязанной позиции.
 *
 * Кандидат — это НЕ сопоставление: резолвер проставляет эталон только на точное
 * совпадение имени, всё похожее складывает сюда. Поэтому у кандидата есть `why`:
 * «две действующие регистрации с одним торговым именем» и «похоже по буквам» —
 * разный уровень доверия, и экран обязан говорить о них разными словами.
 */
export interface MatchCandidate {
  id: string;
  name: string;
  score: number;
  why: 'exact_ambiguous' | 'fuzzy';
}

export interface BudgetItem {
  id: string;
  company_id: string;
  season_id: string;
  section: BudgetSection;
  farm_id: string | null;
  name: string;
  item_kind: string | null;
  tenant_item_id: string | null;
  ref_id: string | null;
  match_status: MatchStatus;
  planned_price: number | null;
  price_unit_id: string | null;
  currency: string;
  price_source: string | null;
  grade: string | null;
  unit_id: string | null;
  source_kind: string;
  notes: string | null;
  archived_at: string | null;
  /** null = резолвер по строке не проходил либо похожего в каталоге не нашлось. */
  match_candidates: MatchCandidate[] | null;
  farm_prices: BudgetFarmPrice[];
  /**
   * Имя записи справочника хозяйства, с которой сопоставлена позиция. В
   * расценках работ это НАЗВАНИЕ ТЕХ.ОПЕРАЦИИ — в колонке «Работа» показываем
   * именно его, потому что в `name` параметры слеплены с работой.
   */
  tenant_item_name?: string | null;
  /** Книга нормативов за позицией. Нет — позиция заведена руками. */
  book?: BudgetBookRef | null;
}

/**
 * То, что потерялось при свёртке книги ЕТС в одну позицию бюджета.
 *
 * Экономист сверяет экран со СВОЕЙ книгой, где строка — это «работа ·
 * параметры · агрегат · ед · расценка · топливо». Позиция показывает медиану
 * по агрегатам, поэтому рядом обязаны стоять параметры отдельной колонкой,
 * разброс и число свёрнутых строк — иначе цифра выглядит выдуманной.
 */
export interface BudgetBookRef {
  params: string | null;
  /** 'field' | 'transport' — полевые и транспортные работы оплачиваются по-разному. */
  work_kind: string | null;
  norms_count: number | null;
  pay_min: number | null;
  pay_max: number | null;
  /** Норма расхода топлива на ту же единицу, что и расценка (л/га, л/ч, л/т). */
  fuel_rate: number | null;
  /** Сколько книг (= кластеров) стоит за позицией. >1 — диапазон межрегиональный. */
  books_count?: number | null;
}

/**
 * Ответ ручки нормативов. НЕ голый массив: число агрегатов на позиции —
 * снимок момента заливки, и после правки единицы или типа операции он
 * расходится с живым запросом. `count_changed` говорит об этом вслух.
 */
export interface BookNormsResponse {
  norms: BookNorm[];
  snapshot_count: number | null;
  count_changed: boolean;
}

/** Строка книги ЕТС: конкретный агрегат со своей расценкой и топливом. */
export interface BookNorm {
  id: string;
  prime_mover: string | null;
  implement: string | null;
  implement_count: number | null;
  width_m: number | null;
  norm_per_shift: number | null;
  pay_per_unit: number | null;
  fuel_rate: number | null;
  machines_ambiguous: boolean;
  executor_hint_raw: string | null;
  source_sheet: string | null;
  source_row: number | null;
}

export interface BudgetItemInput {
  season_id: string;
  name: string;
  farm_id?: string | null;
  item_kind?: string | null;
  ref_id?: string | null;
  planned_price?: number | null;
  price_source?: string | null;
  grade?: string | null;
  unit_id?: string | null;
  notes?: string | null;
  /** Только при явном распоряжении человека — см. BudgetItemDialogSubmit. */
  match_status?: MatchStatus;
}

export type BudgetItemPatch = Partial<Omit<BudgetItemInput, 'season_id' | 'farm_id'>>;

export interface FarmPriceInput {
  farm_id: string;
  price: number;
  price_unit_id?: string | null;
  price_source?: string | null;
}

// ── Ключи и чтение ───────────────────────────────────────────────────────────

export interface BudgetListParams {
  seasonId: string | null;
  section: BudgetSection;
  farmId?: string | null;
  includeArchived?: boolean;
}

export const budgetCatalogKeys = {
  all: () => ['planning', 'budget-catalog'] as const,
  section: (p: BudgetListParams) =>
    [
      'planning',
      'budget-catalog',
      p.seasonId ?? 'none',
      p.section,
      p.farmId ?? 'company',
      p.includeArchived ? 'with-archived' : 'active',
    ] as const,
};

function listUrl(p: BudgetListParams): string {
  const qs = new URLSearchParams({ season_id: p.seasonId as string });
  if (p.farmId) qs.set('farm_id', p.farmId);
  if (p.includeArchived) qs.set('include_archived', 'true');
  return `/api/v1/planning/budget-catalog/${p.section}?${qs.toString()}`;
}

export function budgetSectionQuery(p: BudgetListParams) {
  return {
    queryKey: budgetCatalogKeys.section(p),
    queryFn: () => api.get<BudgetItem[]>(listUrl(p)),
    enabled: !!p.seasonId,
    staleTime: 60_000,
  };
}

export function useBudgetSection(p: BudgetListParams) {
  return useQuery(budgetSectionQuery(p));
}

/**
 * Счётчики на подвкладках ТМЦ — один запрос на категорию, кэш общий с таблицей.
 *
 * `isLoading` — хотя бы одна категория ещё не приехала В ПЕРВЫЙ РАЗ. Нужен
 * тому, кто СУММИРУЕТ счётчики: пока часть запросов в пути, сумма по
 * приехавшим — не «сколько позиций», а «сколько успело доехать», и гейт
 * «справочник пуст» по ней врёт (AK-707). Категория, упавшая с ошибкой,
 * загрузкой не считается — иначе один отказ бэка вешал бы гейт навсегда.
 */
export function useBudgetSectionCounts(
  sections: readonly BudgetSection[],
  base: Omit<BudgetListParams, 'section'>,
): { counts: Partial<Record<BudgetSection, number>>; isLoading: boolean } {
  const results = useQueries({
    queries: sections.map((section) => budgetSectionQuery({ ...base, section })),
  });
  const counts: Partial<Record<BudgetSection, number>> = {};
  sections.forEach((section, i) => {
    const data = results[i]?.data;
    if (data) counts[section] = data.filter((it) => !it.archived_at).length;
  });
  return { counts, isLoading: results.some((r) => r.isLoading) };
}

// ── Запись ───────────────────────────────────────────────────────────────────

function useInvalidate() {
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries({ queryKey: budgetCatalogKeys.all() });
    // Каждая запись сюда двигает статус-машину сезонного справочника на бэке
    // (`budget_catalog.py` зовёт `season_planning.note_catalog_edit` из
    // КАЖДОГО write-эндпоинта — create/patch/archive/farm-price/fill/import):
    // правка после утверждения молча переводит «Утверждён» в «Пересмотрен».
    // Без инвалидации здесь бейдж статуса и хаб-счётчик «N из 6» отставали бы
    // от реальности до истечения staleTime (UAT founder 20.08 — счётчики
    // хаба должны «оживать от реальных статусов», а не от таймера).
    // Широко, без привязки к конкретному season_id/farm_id: хук общий для
    // всех write-мутаций раздела, тянуть сюда контекст сезона ради узкой
    // инвалидации не стоит — запрос сам по себе дешёвый (6 строк).
    //
    // `season-catalogs` — этот экран прямо сейчас показывает его бейдж
    // («Утверждён»/«Пересмотрен»), рефетч нужен СРАЗУ, дефолт остаётся.
    void qc.invalidateQueries({ queryKey: ['planning', 'season-catalogs'] });
    // `season-cards` (хаб «N из 6») здесь НЕ смонтирован — на каждый блюр
    // ценовой ячейки без `refetchType: 'none'` уходил бы лишний фоновый
    // GET списка сезонов (head-web ревью #850). `invalidateQueries` всё
    // равно помечает запрос устаревшим — хаб перечитает актуальные цифры
    // при следующем маунте/фокусе, просто не ПРЯМО СЕЙЧАС.
    void qc.invalidateQueries({ queryKey: ['planning', 'season-cards'], refetchType: 'none' });
  };
}

/**
 * Отказ бэка обязан быть виден ТАМ, где правка сделана. Правка цены идёт
 * прямо в строке — диалога с местом под ошибку в этот момент нет, поэтому
 * 409/422/403 показываем тостом. Без этого несохранённая цифра остаётся в
 * поле и читается как сохранённая (вердикт head-web по #783, блокер 2).
 */
function notifyFailure(err: unknown): void {
  toast.error(budgetErrorText(err));
}

export function useCreateBudgetItem(section: BudgetSection) {
  const invalidate = useInvalidate();
  return useMutation<BudgetItem, Error, BudgetItemInput>({
    mutationFn: (body) => api.post<BudgetItem>(`/api/v1/planning/budget-catalog/${section}`, body),
    onSuccess: invalidate,
    onError: notifyFailure,
  });
}

export function usePatchBudgetItem(section: BudgetSection) {
  const invalidate = useInvalidate();
  return useMutation<BudgetItem, Error, { id: string; patch: BudgetItemPatch }>({
    mutationFn: ({ id, patch }) =>
      api.patch<BudgetItem>(`/api/v1/planning/budget-catalog/${section}/${id}`, patch),
    onSuccess: invalidate,
    onError: notifyFailure,
  });
}

/** «Убрать из бюджета» / «Вернуть» — позиция остаётся в справочнике хозяйства. */
export function useArchiveBudgetItem(section: BudgetSection) {
  const invalidate = useInvalidate();
  return useMutation<BudgetItem, Error, { id: string; restore?: boolean }>({
    mutationFn: ({ id, restore }) =>
      api.post<BudgetItem>(
        `/api/v1/planning/budget-catalog/${section}/${id}/${restore ? 'restore' : 'archive'}`,
        {},
      ),
    onSuccess: invalidate,
    onError: notifyFailure,
  });
}

/** «+ Цена для хозяйства» — исключение на позиции (порция 4 founder). */
export function useUpsertFarmPrice() {
  const invalidate = useInvalidate();
  return useMutation<BudgetItem, Error, { id: string; body: FarmPriceInput }>({
    mutationFn: ({ id, body }) =>
      api.put<BudgetItem>(`/api/v1/planning/budget-catalog/items/${id}/farm-price`, body),
    onSuccess: invalidate,
    onError: notifyFailure,
  });
}

export function useDeleteFarmPrice() {
  const invalidate = useInvalidate();
  return useMutation<BudgetItem, Error, { id: string; farmId: string }>({
    mutationFn: ({ id, farmId }) =>
      api.delete<BudgetItem>(
        `/api/v1/planning/budget-catalog/items/${id}/farm-price?farm_id=${farmId}`,
      ),
    onSuccess: invalidate,
    onError: notifyFailure,
  });
}

// ── Единицы измерения ────────────────────────────────────────────────────────

export interface MeasurementUnit {
  id: string;
  code: string;
  symbol: string;
  name_ru: string | null;
  category: string | null;
}

/**
 * Строки книги ЕТС за позицией — ЛЕНИВО, по раскрытию строки.
 *
 * Перф: за 315 позициями шаблона стоят 3 199 нормативов. Тянуть их вместе со
 * списком значит грузить в десять раз больше данных ради того, что экономист
 * откроет на двух-трёх строках. `enabled` включается только раскрытием,
 * `staleTime` держит уже открытое — повторное схлопывание/раскрытие не ходит
 * в сеть.
 */
export function useItemBookNorms(section: BudgetSection, itemId: string | null) {
  return useQuery<BookNormsResponse, Error>({
    queryKey: ['planning', 'budget-catalog', section, itemId, 'norms'],
    queryFn: () =>
      api.get<BookNormsResponse>(`/api/v1/planning/budget-catalog/${section}/${itemId}/norms`),
    enabled: !!itemId,
    staleTime: 5 * 60_000,
  });
}

export function useMeasurementUnits() {
  return useQuery<MeasurementUnit[], Error>({
    queryKey: ['reference', 'measurement-units'],
    queryFn: () => api.get<MeasurementUnit[]>('/api/v1/reference/pesticides/units'),
    staleTime: 10 * 60_000,
  });
}

/**
 * «Плановая цена, ₽» → «Плановая цена, ₽ за л» / «… за т» / «… за п.е.».
 *
 * UAT founder 20.08: «это руб за л?» — надпись рядом с полем цены не отвечала
 * сама на свой же вопрос. Пусто, если единица ещё не выбрана — подпись не
 * придумывает ответ раньше пользователя.
 */
export function priceUnitSuffix(unit: MeasurementUnit | null | undefined): string {
  return unit ? ` за ${unit.symbol}` : '';
}

// ── Подписи ──────────────────────────────────────────────────────────────────

/** Значок сопоставления в строке (легенда макета). */
export const MATCH_BADGE: Record<
  MatchStatus,
  { label: string; title: string; variant: 'primary' | 'accent-brand' | 'outline' | 'default' }
> = {
  matched: { label: '✓', title: 'сопоставлено с системным справочником', variant: 'primary' },
  suggested: { label: '?', title: 'подсказка системы — подтвердите', variant: 'accent-brand' },
  queued: { label: '?', title: 'ждёт сопоставления', variant: 'accent-brand' },
  pending_approval: { label: '?', title: 'отправлено на разбор', variant: 'accent-brand' },
  not_applicable: { label: '—', title: 'сопоставлять не с чем: системного справочника нет', variant: 'outline' },
  own: { label: 'своя', title: 'своя позиция без эталона — это легально', variant: 'outline' },
};

export function systemCatalogCellText(item: BudgetItem): string {
  if (item.ref_id || item.tenant_item_id) return 'сопоставлено';
  if (item.match_status === 'own') return 'своя позиция, без эталона';
  if (item.match_status === 'not_applicable') return 'системного справочника нет';
  return 'не сопоставлено';
}

/**
 * Текст отказа бэка. Отдельно от общего `describeApiError`, потому что
 * бюджетный слой отдаёт 409 объектом `{code, message, section}` — общий
 * хелпер вернул бы объект, и React упал бы на рендере.
 */
export function budgetErrorText(err: unknown): string {
  const detail = (err as { rawDetail?: { detail?: unknown } })?.rawDetail?.detail;
  if (detail && typeof detail === 'object' && 'message' in detail) {
    return String((detail as { message: unknown }).message);
  }
  return describeApiError(err);
}

// Денежный формат — в `lib/planning/money.ts`: тем же форматом печатают цены
// техкарты и цены реализации, и разъехаться он не должен (AK-704). Ре-экспорт
// оставлен, чтобы экран продолжал импортировать деньги из своего модуля.
export {
  formatFuelRate,
  formatMoneyCell,
  formatMoneyNumber,
  formatPrice,
  formatPricePerUnit,
  parsePriceInput,
} from './money';

// ── Мастер наполнения (макет v2-13) ──────────────────────────────────────────

/**
 * Ветка «Загрузить экселем» подфлоу Б макета:
 *   Б1 скачать образец → Б2 загрузить свой файл → Б3-Б4 окно предпросмотра →
 *   Б5 сохранить.
 *
 * Состояние между предпросмотром и сохранением сервер НЕ держит: строки, что
 * пользователь подтвердил, клиент возвращает обратно (тот же приём, что у
 * массового приглашения). Поэтому «сохранить» — это отдельный список строк, а
 * не «применить предпросмотр номер N».
 */
export type ImportRowStatus = 'new' | 'changed' | 'unchanged' | 'error';

/**
 * Лимит размера файла — ОДНА цифра на подсказку под полем, текст отказа бэка и
 * текст отказа прокси. Разъедутся — человек прочитает противоречие: «обещали
 * 10, ругаетесь на 5».
 */
export const IMPORT_MAX_MB = 10;

/**
 * Отказы загрузки на языке человека.
 *
 * Три случая, которые `describeApiError` показать не может:
 *   • 413 режет nginx РАНЬШЕ приложения и отвечает HTML — `resp.json()` падает,
 *     и в тост уезжает английский дефолт «Request failed»;
 *   • сетевой обрыв на большом файле — `fetch` бросает TypeError без статуса;
 *   • 403 на записи: у читателя есть право на предпросмотр, но не на загрузку,
 *     и он должен понять, что дело в правах, а не в файле.
 */
export function importErrorText(err: unknown): string {
  const status = (err as { status?: number })?.status;
  if (status === 413) {
    return `Файл больше, чем принимает сервер — сократите его до ${IMPORT_MAX_MB} МБ или разбейте на части.`;
  }
  if (status === 403) {
    return 'Не хватает прав: загружать позиции в бюджетный справочник может тот, кому разрешено править цены.';
  }
  if (status === undefined) {
    return 'Не удалось отправить файл — проверьте связь и попробуйте ещё раз.';
  }
  return budgetErrorText(err);
}

export interface ImportColumn {
  source: string;
  field: string | null;
  title: string | null;
  recognized: boolean;
}

export interface ImportRow {
  row_number: number;
  status: ImportRowStatus;
  name: string;
  item_kind: string | null;
  grade: string | null;
  notes: string | null;
  planned_price: number | null;
  price_source: string | null;
  unit_text: string | null;
  unit_id: string | null;
  item_id: string | null;
  changes: string[];
  errors: string[];
}

export interface ImportPreview {
  section: BudgetSection;
  file_name: string;
  columns: ImportColumn[];
  rows: ImportRow[];
  counts: { total: number; new: number; changed: number; unchanged: number; error: number };
}

export interface ImportCommitResult {
  section: BudgetSection;
  created: number;
  updated: number;
  unchanged: number;
}

export const IMPORT_STATUS_TITLES: Record<ImportRowStatus, string> = {
  new: 'Новые',
  changed: 'Изменённые',
  unchanged: 'Без изменений',
  error: 'С ошибкой',
};

/** Строки, которые реально поедут в справочник: без ошибок и не «как было». */
export function importableRows(rows: ImportRow[]): ImportRow[] {
  return rows.filter((r) => r.status === 'new' || r.status === 'changed');
}

/**
 * Образец качаем blob'ом, а не `<a download>`: сессия у нас в cookie, но имя
 * файла и разбор ошибки (403 без права) нужны в JS — иначе браузер молча
 * откроет страницу логина вместо файла.
 */
export async function downloadImportTemplate(section: BudgetSection): Promise<void> {
  const resp = await fetch(
    `${API_URL}/api/v1/planning/budget-catalog/${section}/import-template`,
    { credentials: 'include' },
  );
  if (!resp.ok) {
    // Раньше сюда уезжал машинный код `template_download_failed`, и человек
    // читал его в тосте дословно.
    const message =
      resp.status === 403
        ? 'Образец доступен тем, кому открыт бюджетный справочник — попросите доступ у администратора.'
        : resp.status === 404
          ? 'Для этого раздела образца пока нет — заведите первую позицию вручную.'
          : 'Не удалось скачать образец — попробуйте ещё раз.';
    throw new ApiError(resp.status, message);
  }
  const blob = await resp.blob();
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `образец-${SECTION_TITLES[section]}.xlsx`;
  document.body.appendChild(link);
  link.click();
  link.remove();
  // Safari/Firefox обрывают скачивание, если ссылку отозвать в том же тике.
  setTimeout(() => URL.revokeObjectURL(url), 0);
}

export function useImportPreview(section: BudgetSection) {
  return useMutation<ImportPreview, Error, { file: File; seasonId: string; farmId: string | null }>({
    mutationFn: ({ file, seasonId, farmId }) => {
      const form = new FormData();
      form.append('file', file);
      form.append('season_id', seasonId);
      if (farmId) form.append('farm_id', farmId);
      return api.uploadFile<ImportPreview>(
        `/api/v1/planning/budget-catalog/${section}/import/preview`,
        form,
      );
    },
    onError: (err) => toast.error(importErrorText(err)),
  });
}

export function useImportCommit(section: BudgetSection) {
  const invalidate = useInvalidate();
  return useMutation<
    ImportCommitResult,
    Error,
    { seasonId: string; farmId: string | null; rows: ImportRow[] }
  >({
    mutationFn: ({ seasonId, farmId, rows }) =>
      api.post<ImportCommitResult>(
        `/api/v1/planning/budget-catalog/${section}/import/commit`,
        {
          season_id: seasonId,
          farm_id: farmId,
          rows: rows.map((r) => ({
            row_number: r.row_number,
            name: r.name,
            item_kind: r.item_kind,
            grade: r.grade,
            notes: r.notes,
            planned_price: r.planned_price,
            price_source: r.price_source,
            unit_id: r.unit_id,
          })),
        },
      ),
    onSuccess: invalidate,
    onError: (err) => toast.error(importErrorText(err)),
  });
}

export interface FillResult {
  section: BudgetSection;
  created: number;
  skipped_existing: number;
}

/** Способы «взять из справочников компании» и «взять из прошлого бюджета». */
export function useFillSection(section: BudgetSection) {
  const invalidate = useInvalidate();
  return useMutation<
    FillResult,
    Error,
    {
      seasonId: string;
      mode: 'copy_season' | 'from_tenant_catalog' | 'from_company_template';
      fromSeasonId?: string | null;
      farmId?: string | null;
      keepFarmLevel?: boolean;
      resetPrices?: boolean;
    }
  >({
    mutationFn: ({ seasonId, mode, fromSeasonId, farmId, keepFarmLevel, resetPrices }) =>
      api.post<FillResult>(`/api/v1/planning/budget-catalog/${section}/fill`, {
        season_id: seasonId,
        mode,
        from_season_id: fromSeasonId ?? null,
        farm_id: farmId ?? null,
        keep_farm_level: keepFarmLevel ?? true,
        reset_prices: resetPrices ?? false,
      }),
    onSuccess: invalidate,
    onError: notifyFailure,
  });
}
